import {
  WORLD_GENERATION_SEED,
  WORLD_GENERATION_VERSION,
  generateWorldObjectChunk,
  getAvailableInteractionActions,
  getGeneratedCollisionCacheSize,
  getInteractionProfile,
  getLargeWorldCollisionCandidatesAt,
  hasInteractionLineOfSight,
  isWorldChunkInBounds,
  sampleMacroBiomeAt,
  sampleWorldEcologyAt,
  sampleWorldRadiationAt,
  sampleWorldRegionWeightsAt,
  worldToChunkCoordinate,
  type AIObject,
  type GeologyObject,
  type HumanObject,
  type VegetationObject,
  type WorldObjectChunk,
  type InteractableDescriptor,
  type WorldPoi,
  type WorldRegionWeights,
  type MacroBiomeSample,
  type WorldEcologicalMetadata,
  type WorldRadiationSample,
} from "@endclock/shared";
import type { AIWorld } from "../ai/ai.js";
import type { GeologyWorld } from "../geology/geology.js";
import type { HumanWorld } from "../human/human.js";
import type { HeightfieldTerrain } from "../terrain/terrain.js";
import type { VegetationWorld } from "../vegetation/vegetation.js";
import type { InteractionCandidate } from "../interaction/target-selection.js";
import { CHUNK_PREFETCH_RADIUS, TERRAIN_GPU_RADIUS, chunkDistance, prioritizedChunkWindow } from "./chunk-streaming.js";
import { ClientWorldOverrideCache } from "./world-overrides.js";

interface StreamedInteractable {
  readonly descriptor: InteractableDescriptor;
  readonly objectType: DeveloperObjectRecord["type"];
}

export interface WorldObjectStreamingSnapshot {
  readonly seed: number;
  readonly generationVersion: number;
  readonly cachedChunks: number;
  readonly activeChunks: number;
  readonly readyActiveChunks: number;
  readonly pendingChunks: number;
  readonly failedChunks: number;
  readonly lastError?: string;
  readonly generationMsAverage: number;
  readonly generationMsMax: number;
  readonly currentRegions: WorldRegionWeights;
  readonly currentBiome: MacroBiomeSample;
  readonly radiation: WorldRadiationSample;
  readonly ecology: WorldEcologicalMetadata;
  readonly nearestPoi: string | null;
  readonly nearbyHumanSites: readonly string[];
  readonly nearbyAiSites: readonly string[];
  readonly stableObjectCount: number;
  readonly collisionCacheChunks: number;
}

export interface DeveloperPoiRecord {
  readonly id: string;
  readonly origin: "human" | "ai";
  readonly archetype: string;
  readonly x: number;
  readonly z: number;
  readonly footprintRadius: number;
  readonly exclusionRadius: number;
}

export interface DeveloperObjectRecord {
  readonly id: string;
  readonly type: "geology" | "vegetation" | "human" | "ai";
  readonly asset: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly chunkX: number;
  readonly chunkZ: number;
  readonly yaw: number;
  readonly scale: number;
  readonly poiParent?: string;
}

export interface DeveloperWorldRecords {
  readonly pois: readonly DeveloperPoiRecord[];
  readonly objects: readonly DeveloperObjectRecord[];
}

/** Bounded CPU-side chunk content; global GLB meshes/textures stay shared. */
export class WorldObjectStreamer {
  private readonly cache = new Map<string, WorldObjectChunk>();
  private activeIds: readonly string[] = [];
  private pending: readonly { readonly x: number; readonly z: number; readonly id: string }[] = [];
  private observedTerrainRevision = -1;
  private generatedChunks = 0;
  private readonly failed = new Set<string>();
  private lastError: string | undefined;
  private generationMsTotal = 0;
  private generationMsMax = 0;
  private currentRegions = sampleWorldRegionWeightsAt(0, 12);
  private currentBiome = sampleMacroBiomeAt(0, 12);
  private radiation = sampleWorldRadiationAt(0, 12);
  private ecology = sampleWorldEcologyAt(0, 12);
  private playerX = 0;
  private playerZ = 12;
  private environmentSampleX = 0;
  private environmentSampleZ = 12;
  private nearestPoi: string | null = null;
  private nearbyHumanSites: readonly string[] = [];
  private nearbyAiSites: readonly string[] = [];
  private stableObjectCount = 0;
  private debugRecords: DeveloperWorldRecords = { pois: [], objects: [] };
  private readonly interactablesByChunk = new Map<string, StreamedInteractable[]>();
  private readonly interactionCandidateScratch: InteractionCandidate[] = [];

  constructor(
    private readonly terrain: HeightfieldTerrain,
    private readonly geology: GeologyWorld,
    private readonly vegetation: VegetationWorld,
    private readonly human: HumanWorld,
    private readonly ai: AIWorld,
    private readonly overrides = new ClientWorldOverrideCache(),
  ) {
    this.rebuildWindow(true);
  }

  get macroBiome(): MacroBiomeSample {
    return this.currentBiome;
  }

  update(playerX: number, playerZ: number): void {
    this.playerX = playerX;
    this.playerZ = playerZ;
    if (Math.hypot(playerX - this.environmentSampleX, playerZ - this.environmentSampleZ) >= 2) {
      this.environmentSampleX = playerX;
      this.environmentSampleZ = playerZ;
      this.currentBiome = sampleMacroBiomeAt(playerX, playerZ);
      this.currentRegions = sampleWorldRegionWeightsAt(playerX, playerZ);
      this.radiation = sampleWorldRadiationAt(playerX, playerZ);
      this.ecology = sampleWorldEcologyAt(playerX, playerZ);
    }
    if (this.observedTerrainRevision !== this.terrain.revision) this.rebuildWindow(false);
    const next = this.pending[0];
    if (next) {
      const affectsActiveContent = this.activeIds.includes(next.id);
      this.generate(next.x, next.z, next.id);
      this.pending = this.pending.slice(1);
      if (affectsActiveContent) this.publishActiveContent();
    }
  }

  snapshot(): WorldObjectStreamingSnapshot {
    return {
      seed: WORLD_GENERATION_SEED,
      generationVersion: WORLD_GENERATION_VERSION,
      cachedChunks: this.cache.size,
      activeChunks: this.activeIds.length,
      readyActiveChunks: this.activeIds.reduce((count, id) => count + (this.cache.has(id) ? 1 : 0), 0),
      pendingChunks: this.pending.length,
      failedChunks: this.failed.size,
      ...(this.lastError ? { lastError: this.lastError } : {}),
      generationMsAverage: this.generatedChunks > 0 ? this.generationMsTotal / this.generatedChunks : 0,
      generationMsMax: this.generationMsMax,
      currentRegions: this.currentRegions,
      currentBiome: this.currentBiome,
      radiation: this.radiation,
      ecology: this.ecology,
      nearestPoi: this.nearestPoi,
      nearbyHumanSites: this.nearbyHumanSites,
      nearbyAiSites: this.nearbyAiSites,
      stableObjectCount: this.stableObjectCount,
      collisionCacheChunks: getGeneratedCollisionCacheSize(),
    };
  }

  developerRecords(): DeveloperWorldRecords {
    return this.debugRecords;
  }

  interactionCandidatesAt(
    origin: readonly [x: number, y: number, z: number],
    searchRadius = 4,
  ): readonly InteractionCandidate[] {
    const candidates = this.interactionCandidateScratch;
    candidates.length = 0;
    const centerX = worldToChunkCoordinate(origin[0]);
    const centerZ = worldToChunkCoordinate(origin[2]);
    let colliders: ReturnType<typeof getLargeWorldCollisionCandidatesAt> | undefined;
    for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
      for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
        const bucket = this.interactablesByChunk.get(`${centerX + offsetX}:${centerZ + offsetZ}`);
        if (!bucket) continue;
        for (const streamed of bucket) {
          const descriptor = streamed.descriptor;
          const anchorDistance = Math.hypot(
            descriptor.worldAnchor[0] - origin[0],
            descriptor.worldAnchor[1] - origin[1],
            descriptor.worldAnchor[2] - origin[2],
          );
          if (anchorDistance > searchRadius) continue;
          const override = this.overrides.get(descriptor.objectId);
          const profile = getInteractionProfile(descriptor.profileId);
          const objectState = override?.state ?? profile.initialState;
          if (objectState === "REMOVED") continue;
          const actions = getAvailableInteractionActions(profile, objectState);
          let action = actions[0];
          for (let index = 1; index < actions.length; index += 1) {
            if (actions[index]!.priority > (action?.priority ?? -Infinity)) action = actions[index];
          }
          if (!action) continue;
          const lineOfSight = !action.requiresLineOfSight || hasInteractionLineOfSight(
            origin,
            descriptor.worldAnchor,
            descriptor.objectId,
            colliders ??= getLargeWorldCollisionCandidatesAt(origin[0], origin[2]),
          );
          candidates.push({
            target: { kind: "WORLD_OBJECT", id: descriptor.objectId },
            objectType: streamed.objectType,
            profileId: descriptor.profileId,
            displayName: profile.displayName,
            actionId: action.id,
            actionLabel: action.label,
            durationMs: action.durationMs,
            interruptible: action.interruptible,
            anchorX: descriptor.worldAnchor[0],
            anchorY: descriptor.worldAnchor[1],
            anchorZ: descriptor.worldAnchor[2],
            interactionRadius: descriptor.interactionRadius,
            maxRange: action.maxRange,
            priority: action.priority,
            objectState,
            revision: override?.revision ?? 0,
            lineOfSight,
            available: true,
          });
        }
      }
    }
    return candidates;
  }

  refreshWorldOverrides(): void {
    this.publishActiveContent();
  }

  private rebuildWindow(warm: boolean): void {
    this.observedTerrainRevision = this.terrain.revision;
    const stream = this.terrain.snapshot();
    const center = stream.currentChunk;
    const active = prioritizedChunkWindow(center.x, center.z, TERRAIN_GPU_RADIUS).filter((chunk) =>
      isWorldChunkInBounds(chunk.x, chunk.z),
    );
    this.activeIds = active.map((chunk) => chunk.id);
    const prefetch = prioritizedChunkWindow(center.x, center.z, CHUNK_PREFETCH_RADIUS).filter((chunk) =>
      isWorldChunkInBounds(chunk.x, chunk.z),
    );
    const retainedIds = new Set(prefetch.map((chunk) => chunk.id));
    for (const [id, content] of this.cache) {
      const coordinates = content.source.metadata.chunk;
      if (chunkDistance(coordinates.x, coordinates.z, center.x, center.z) > CHUNK_PREFETCH_RADIUS) this.cache.delete(id);
    }
    for (const id of this.failed) if (!retainedIds.has(id)) this.failed.delete(id);
    const missing = prefetch.filter((chunk) => !this.cache.has(chunk.id) && !this.failed.has(chunk.id));
    if (warm) {
      for (const chunk of active) this.generate(chunk.x, chunk.z, chunk.id);
      this.pending = missing.filter((chunk) => !this.cache.has(chunk.id));
    } else {
      this.pending = missing;
    }
    this.publishActiveContent();
  }

  private generate(chunkX: number, chunkZ: number, id: string): void {
    if (this.cache.has(id) || this.failed.has(id)) return;
    const startedAt = performance.now();
    try {
      const content = generateWorldObjectChunk(chunkX, chunkZ);
      this.cache.set(id, content);
    } catch (error) {
      // A composition failure must not block navigable terrain or later chunks.
      this.failed.add(id);
      this.lastError = `${id}: ${error instanceof Error ? error.message : "Unknown object-composition error"}`;
    } finally {
      const elapsed = performance.now() - startedAt;
      this.generatedChunks += 1;
      this.generationMsTotal += elapsed;
      this.generationMsMax = Math.max(this.generationMsMax, elapsed);
    }
  }

  private publishActiveContent(): void {
    const geology: GeologyObject[] = [];
    const vegetation: VegetationObject[] = [];
    const human: HumanObject[] = [];
    const ai: AIObject[] = [];
    const humanSites: WorldPoi[] = [];
    const aiSites: WorldPoi[] = [];
    this.interactablesByChunk.clear();
    for (const id of this.activeIds) {
      const chunk = this.cache.get(id);
      if (!chunk) continue;
      for (const object of chunk.geology) if (this.overrides.get(object.id)?.state !== "REMOVED") geology.push(object);
      for (const object of chunk.vegetation) if (this.overrides.get(object.id)?.state !== "REMOVED") vegetation.push(object);
      for (const object of chunk.human) {
        const state = this.overrides.get(object.id)?.state;
        if (state === "REMOVED") continue;
        human.push(state === "OPEN" && object.assetId === "supply_cache_closed"
          ? { ...object, assetId: "supply_cache_open" }
          : object);
      }
      for (const object of chunk.ai) if (this.overrides.get(object.id)?.state !== "REMOVED") ai.push(object);
      for (const descriptor of chunk.interactables) {
        const objectType = objectTypeInChunk(chunk, descriptor.objectId);
        if (!objectType) continue;
        const key = `${descriptor.ownerChunk[0]}:${descriptor.ownerChunk[1]}`;
        const bucket = this.interactablesByChunk.get(key);
        const streamed = { descriptor, objectType };
        if (bucket) bucket.push(streamed);
        else this.interactablesByChunk.set(key, [streamed]);
      }
      humanSites.push(...chunk.source.humanPois);
      aiSites.push(...chunk.source.aiPois);
    }
    this.geology.setObjects(geology);
    this.vegetation.setObjects(vegetation);
    this.human.setObjects(human);
    this.ai.setObjects(ai);
    this.nearbyHumanSites = Object.freeze(humanSites.map((site) => `${site.archetype}:${site.id}`));
    this.nearbyAiSites = Object.freeze(aiSites.map((site) => `${site.archetype}:${site.id}`));
    const nearest = [...humanSites, ...aiSites].reduce<WorldPoi | undefined>((best, site) => {
      if (!best) return site;
      return Math.hypot(site.x - this.playerX, site.z - this.playerZ) < Math.hypot(best.x - this.playerX, best.z - this.playerZ)
        ? site
        : best;
    }, undefined);
    this.nearestPoi = nearest
      ? `${nearest.archetype}:${nearest.id} ${Math.hypot(nearest.x - this.playerX, nearest.z - this.playerZ).toFixed(0)}m`
      : null;
    this.stableObjectCount = geology.length + vegetation.length + human.length + ai.length + humanSites.length + aiSites.length;
    this.debugRecords = {
      pois: [...humanSites, ...aiSites].map((site) => ({
        id: site.id,
        origin: site.origin,
        archetype: site.archetype,
        x: site.x,
        z: site.z,
        footprintRadius: site.footprintRadius,
        exclusionRadius: site.exclusionRadius,
      })),
      objects: [
        ...geology.map((object) => ({
          id: object.id, type: "geology" as const, asset: object.assetId,
          x: object.x, y: object.y, z: object.z, chunkX: object.chunkX, chunkZ: object.chunkZ,
          yaw: object.yaw, scale: object.scale,
        })),
        ...vegetation.map((object) => ({
          id: object.id, type: "vegetation" as const, asset: object.assetId,
          x: object.x, y: object.y, z: object.z, chunkX: object.chunkX, chunkZ: object.chunkZ,
          yaw: object.yaw, scale: object.scale,
        })),
        ...human.map((object) => ({
          id: object.id, type: "human" as const, asset: object.assetId,
          x: object.x, y: object.y, z: object.z, chunkX: object.chunkX, chunkZ: object.chunkZ,
          yaw: object.yaw, scale: object.scale, poiParent: object.siteId,
        })),
        ...ai.map((object) => ({
          id: object.id, type: "ai" as const, asset: object.assetId,
          x: object.x, y: object.y, z: object.z, chunkX: object.chunkX, chunkZ: object.chunkZ,
          yaw: object.yaw, scale: object.scale, poiParent: object.siteId,
        })),
      ],
    };
  }
}

function objectTypeInChunk(
  chunk: WorldObjectChunk,
  objectId: string,
): DeveloperObjectRecord["type"] | undefined {
  if (chunk.geology.some((object) => object.id === objectId)) return "geology";
  if (chunk.vegetation.some((object) => object.id === objectId)) return "vegetation";
  if (chunk.human.some((object) => object.id === objectId)) return "human";
  if (chunk.ai.some((object) => object.id === objectId)) return "ai";
  return undefined;
}
